import { getRulesModule } from './contests/getRulesModule';

export {
  getActiveContacts,
  getContactOperator,
  collectOperators,
  buildQsoTotalsByBandMode
} from './cabrilloExport';

export const AZQP_SUBMIT_URL = 'https://azqp.contesting.com/azqpsubmitlog.php';

function cabrilloMode(mode) {
  if (mode === 'Phone') return 'PH';
  if (mode === 'CW') return 'CW';
  return 'PH';
}

function cabrilloFrequency(band) {
  const map = {
    160: '1800',
    80: '3500',
    40: '7000',
    20: '14000',
    15: '21000',
    10: '28000'
  };
  return map[String(band || '').replace(/\s*\([^)]*\)$/, '').trim()] || '14000';
}

export function validateAzqpCabrilloExport({ settings, contacts, clubName }) {
  const rules = getRulesModule('azqp');
  const normalized = rules.normalizeStationSettings(settings);
  const errors = [];
  const warnings = [];
  const activeContacts = (contacts || []).filter((contact) => contact.deleted !== 'Y');

  if (!(normalized.callsign || '').trim()) {
    errors.push('Entry callsign is required (Station Settings).');
  }

  if (!normalized.sentLocation) {
    errors.push('Sent exchange location is required (AZ county or state/province).');
  }

  if (activeContacts.length === 0) {
    errors.push('No active contacts to export.');
  }

  activeContacts.forEach((contact, index) => {
    if (!contact.callsign) {
      warnings.push(`Contact #${index + 1} is missing a callsign.`);
    }
    if (!rules.validateRst(contact.classSent)) {
      warnings.push(`${contact.callsign || `Contact #${index + 1}`}: missing or invalid RST received.`);
    }
    if (!rules.validateAzqpExchange(contact.locationReceived)) {
      warnings.push(`${contact.callsign || `Contact #${index + 1}`}: invalid received exchange.`);
    }
  });

  return {
    errors,
    warnings,
    settings: normalized,
    activeContactCount: activeContacts.length,
    clubName
  };
}

export function generateAzqpCabrilloLog({
  contacts,
  settings,
  clubName,
  softwareVersion = 'CQ Rush'
}) {
  const rules = getRulesModule('azqp');
  const validation = validateAzqpCabrilloExport({ settings, contacts, clubName });

  if (validation.errors.length > 0) {
    throw new Error(validation.errors.join(' '));
  }

  const normalized = validation.settings;
  const activeContacts = (contacts || [])
    .filter((contact) => contact.deleted !== 'Y')
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const callsign = normalized.callsign.trim().toUpperCase();
  const sentRst = normalized.defaultSentRst || '59';
  const sentLocation = normalized.sentLocation;
  const projected = rules.calculateProjectedScore(activeContacts, normalized);

  const lines = [
    `CALLSIGN: ${callsign}`,
    'CONTEST: AZ-QSO-PARTY',
    `CATEGORY-OPERATOR: ${normalized.entryCategory.includes('MULTI') ? 'MULTI-OP' : 'SINGLE-OP'}`,
    'CATEGORY-ASSISTED: ASSISTED',
    'CATEGORY-BAND: ALL',
    `CATEGORY-MODE: ${normalized.modeCategory}`,
    `CATEGORY-POWER: ${normalized.powerLevel}`,
    `CATEGORY-STATION: ${normalized.stationCategory}`,
    ...(clubName ? [`CLUB: ${String(clubName).slice(0, 60)}`] : []),
    `CREATED-BY: ${softwareVersion}`,
    `CLAIMED-SCORE: ${projected.finalScore}`,
    `SOAPBOX: Generated by CQ Rush. Submit at ${AZQP_SUBMIT_URL}`,
    'START-OF-LOG:'
  ];

  activeContacts.forEach((contact) => {
    const timestamp = new Date(contact.timestamp);
    const date = timestamp.toISOString().slice(0, 10);
    const time = timestamp.toISOString().slice(11, 16).replace(':', '');
    const freq = cabrilloFrequency(contact.frequency);
    const mode = cabrilloMode(contact.mode);
    const receivedRst = String(contact.classSent || '').trim();
    const receivedExchange = String(contact.locationReceived || '').trim().toUpperCase();

    lines.push(
      `QSO: ${freq} ${mode} ${date} ${time} ${callsign} ${sentRst} ${sentLocation} ${(contact.callsign || '').toUpperCase()} ${receivedRst} ${receivedExchange}`
    );
  });

  lines.push('END-OF-LOG:');

  return {
    content: `${lines.join('\r\n')}\r\n`,
    validation,
    projected,
    callsign
  };
}

export function buildAzqpEntrySummary({ contacts, settings, clubName }) {
  const rules = getRulesModule('azqp');
  const normalized = rules.normalizeStationSettings(settings);
  const activeContacts = (contacts || []).filter((contact) => contact.deleted !== 'Y');
  const projected = rules.calculateProjectedScore(activeContacts, normalized);
  const breakdown = rules.calculateScoreBreakdown(activeContacts);
  const exchange = rules.formatExchange(normalized);

  return {
    entryUrl: AZQP_SUBMIT_URL,
    callUsed: normalized.callsign,
    clubName,
    exchange,
    entryCategory: normalized.entryCategory,
    powerLevel: normalized.powerLevel,
    modeCategory: normalized.modeCategory,
    stationCategory: normalized.stationCategory,
    claimedScore: projected.finalScore,
    qsoPoints: projected.qsoPoints,
    multipliers: projected.multipliers.total,
    bonusPoints: projected.bonusPoints,
    breakdown,
    activeContactCount: activeContacts.length
  };
}

export function formatAzqpEntrySummaryText(summary) {
  if (!summary) {
    return '';
  }

  return [
    `Arizona QSO Party — ${summary.callUsed || 'Entry'}`,
    `Exchange sent: ${summary.exchange || '—'}`,
    `Category: ${summary.entryCategory} / ${summary.modeCategory} / ${summary.powerLevel} / ${summary.stationCategory}`,
    `QSO points: ${summary.qsoPoints}`,
    `Multipliers: ${summary.multipliers}`,
    `Bonus: ${summary.bonusPoints}`,
    `Claimed score: ${summary.claimedScore}`,
    `Submit: ${summary.entryUrl}`
  ].join('\n');
}

export function downloadAzqpCabrilloFile(content, callsign) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(callsign || 'azqp').toLowerCase()}.cbr`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
